import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import { useSetAtom } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import Toast from '@/utils/toastManager';
import axiosConfig from '@/data/utils/axiosConfigInstance';
import { trackPaymentUiClick } from '@/data/utils/paymentUiAnalytics';
import { getAllTrackingIds, persistMetaCookiesToStorage } from '@/utils/metaCookies';
import './CustomCheckoutModal.css';

const COUNTRIES = [
	{ code: 'US', name: 'United States' },
	{ code: 'CA', name: 'Canada' },
	{ code: 'GB', name: 'United Kingdom' },
	{ code: 'AU', name: 'Australia' },
	{ code: 'NZ', name: 'New Zealand' },
	{ code: 'IN', name: 'India' },
	{ code: 'AF', name: 'Afghanistan' },
	{ code: 'AL', name: 'Albania' },
	{ code: 'DZ', name: 'Algeria' },
	{ code: 'AR', name: 'Argentina' },
	{ code: 'AT', name: 'Austria' },
	{ code: 'BE', name: 'Belgium' },
	{ code: 'BR', name: 'Brazil' },
	{ code: 'CN', name: 'China' },
	{ code: 'DK', name: 'Denmark' },
	{ code: 'EG', name: 'Egypt' },
	{ code: 'FI', name: 'Finland' },
	{ code: 'FR', name: 'France' },
	{ code: 'DE', name: 'Germany' },
	{ code: 'GR', name: 'Greece' },
	{ code: 'HK', name: 'Hong Kong' },
	{ code: 'IE', name: 'Ireland' },
	{ code: 'IL', name: 'Israel' },
	{ code: 'IT', name: 'Italy' },
	{ code: 'JP', name: 'Japan' },
	{ code: 'MX', name: 'Mexico' },
	{ code: 'NL', name: 'Netherlands' },
	{ code: 'NO', name: 'Norway' },
	{ code: 'PL', name: 'Poland' },
	{ code: 'PT', name: 'Portugal' },
	{ code: 'RU', name: 'Russia' },
	{ code: 'SA', name: 'Saudi Arabia' },
	{ code: 'SG', name: 'Singapore' },
	{ code: 'ZA', name: 'South Africa' },
	{ code: 'KR', name: 'South Korea' },
	{ code: 'ES', name: 'Spain' },
	{ code: 'SE', name: 'Sweden' },
	{ code: 'CH', name: 'Switzerland' },
	{ code: 'TH', name: 'Thailand' },
	{ code: 'TR', name: 'Turkey' },
	{ code: 'AE', name: 'United Arab Emirates' }
];

const getPaymentMethodId = (method, fallbackIndex) =>
	method?.reference_id || method?.id || `${method?.gateway || 'method'}-${fallbackIndex}`;

const getNormalizedPaymentMethodType = method => {
	const rawType = `${method?.type || ''}`.toLowerCase();
	const gateway = `${method?.gateway || ''}`.toLowerCase();

	if (rawType.includes('paypal') || gateway.includes('paypal')) return 'paypal';
	if (rawType.includes('apple') || gateway.includes('apple')) return 'apple_pay';
	if (rawType === 'card' || rawType === 'ccard' || gateway.includes('stripe') || gateway.includes('card'))
		return 'card';

	return rawType || gateway || 'payment_method';
};

const getSavedPaymentMethodLabel = method => {
	if (method?.displayLabel) return method.displayLabel;

	const normalizedType = getNormalizedPaymentMethodType(method);
	const gatewayLabel = method?.gateway ? ` via ${method.gateway}` : '';

	if (normalizedType === 'paypal') {
		const maskedEmail = method?.paypal?.email || method?.email || method?.masked_email || method?.customer_email;

		return maskedEmail ? `PayPal saved (${maskedEmail})` : `PayPal saved${gatewayLabel}`;
	}

	if (normalizedType === 'apple_pay') {
		const cardBrand = method?.card?.brand || method?.brand;
		const last4 = method?.card?.last4 || method?.last4;
		return cardBrand && last4
			? `Apple Pay saved (${cardBrand.toUpperCase()} ending in ${last4})`
			: `Apple Pay saved${gatewayLabel}`;
	}

	if (normalizedType === 'card') {
		const cardBrand = method?.card?.brand || method?.brand || 'Card';
		const last4 = method?.card?.last4 || method?.last4 || method?.masked_card_number?.slice(-4);
		const expMonth = method?.card?.exp_month || method?.exp_month;
		const expYear = method?.card?.exp_year || method?.exp_year;

		if (last4) {
			const expiry = expMonth && expYear ? ` (${expMonth}/${expYear})` : '';
			return `${cardBrand.toUpperCase()} ending in ${last4}${expiry}`;
		}

		return `${cardBrand.toUpperCase()} saved${gatewayLabel}`;
	}

	return `${normalizedType.replace(/_/g, ' ')} saved${gatewayLabel}`;
};

const getPlanAmountInCents = plan => {
	const amount = Math.round(Number(plan?.price || 0) * 100);
	return amount > 0 ? amount : 100;
};

const formatMinorUnitAmount = (amount, currencyCode = 'USD') =>
	new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: currencyCode || 'USD'
	}).format((Number(amount) || 0) / 100);

const formatUnixDate = timestamp => {
	if (!timestamp) return 'Not scheduled';

	return new Date(timestamp * 1000).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric'
	});
};

const getBillingCycleLabel = plan => {
	const rawValue = `${plan?.selectedCycle || plan?.cbItem || plan?.id || ''}`.toLowerCase();

	if (rawValue.includes('year') || rawValue.includes('annual')) return 'Annual';
	if (rawValue.includes('month')) return 'Monthly';
	if (rawValue.includes('week')) return 'Weekly';
	if (rawValue.includes('day')) return 'Daily';

	return 'Monthly';
};

const resolvePlanId = plan => {
	const candidate = plan?.cbItem ?? plan?.id ?? null;
	if (Array.isArray(candidate)) {
		return candidate.find(item => typeof item === 'string' && item.trim()) || null;
	}
	if (typeof candidate === 'string') {
		const trimmed = candidate.trim();
		return trimmed || null;
	}
	return null;
};

export default function CustomCheckoutModal({ open, onClose, plan, isCancelledUser = false }) {
	const navigate = useNavigate();
	const setUserData = useSetAtom(userInfoAtom);
	const [loading, setLoading] = useState(false);
	const [showSuccessScreen, setShowSuccessScreen] = useState(false);
	const [dataLoading, setDataLoading] = useState(false);
	const [estimateLoading, setEstimateLoading] = useState(false);
	const [updateEstimate, setUpdateEstimate] = useState(null);
	const [estimateError, setEstimateError] = useState('');
	const [subscriptionStatus, setSubscriptionStatus] = useState(null);
	const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
	const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
	const [selectedPaymentOption, setSelectedPaymentOption] = useState('saved');
	const [selectedNewPaymentType, setSelectedNewPaymentType] = useState('card');
	const [paypalLoading, setPaypalLoading] = useState(false);
	const [applePayAvailable, setApplePayAvailable] = useState(false);
	const paypalInitialized = useRef(false);
	const applePayMounted = useRef(false);
	const applePayContainerRef = useRef(null);
	const [paymentData, setPaymentData] = useState({
		cardholderName: '',
		cardNumber: '',
		cardExpiry: '',
		cardCvv: '',
		zipCode: '',
		country: 'US'
	});
	const [errors, setErrors] = useState({});
	const [couponCode, setCouponCode] = useState('');
	const [couponApplied, setCouponApplied] = useState(false);
	const [couponLoading, setCouponLoading] = useState(false);
	const [couponError, setCouponError] = useState('');
	const [couponEstimate, setCouponEstimate] = useState(null);
	const resolvedIsCancelledUser = subscriptionStatus ? subscriptionStatus?.status === 'cancelled' : isCancelledUser;
	const resolvedPlanId = resolvePlanId(plan);

	const fetchSubscriptionData = async () => {
		const userData = JSON.parse(localStorage.getItem('userData') || '{}');
		if (!userData.email) return;

		const statusResponse = await axiosConfig.get(`${import.meta.env.VITE_APP_API_URL}/chargebee/subscription-status`, {
			params: { email: userData.email }
		});
		setSubscriptionStatus(statusResponse.data);

		// For cancelled users, still fetch saved payment methods from Chargebee
		const shouldFetchPaymentMethods =
			statusResponse.data?.hasSubscription || statusResponse.data?.status === 'cancelled' || isCancelledUser;

		if (!shouldFetchPaymentMethods) {
			setSavedPaymentMethods([]);
			setSelectedPaymentMethodId(null);
			return;
		}

		const paymentMethodsResponse = await axiosConfig.get(
			`${import.meta.env.VITE_APP_API_URL}/chargebee/payment-methods`,
			{ params: { email: userData.email } }
		);
		const paymentMethods = paymentMethodsResponse.data?.paymentMethods || [];
		setSavedPaymentMethods(paymentMethods);
		setSelectedPaymentMethodId(paymentMethods.length > 0 ? getPaymentMethodId(paymentMethods[0], 0) : null);
		setSelectedPaymentOption(paymentMethods.length > 0 ? 'saved' : 'new');
	};

	const fetchUpdateEstimate = async () => {
		const userData = JSON.parse(localStorage.getItem('userData') || '{}');
		if (!userData.email || !resolvedPlanId) {
			setUpdateEstimate(null);
			setEstimateError('');
			return;
		}

		setEstimateLoading(true);
		setEstimateError('');

		try {
			const endpoint = resolvedIsCancelledUser
				? `${import.meta.env.VITE_APP_API_URL}/chargebee/estimate-with-coupon`
				: `${import.meta.env.VITE_APP_API_URL}/chargebee/update-subscription-estimate`;

			const response = await axiosConfig.post(endpoint, {
				email: userData.email,
				planId: resolvedPlanId
			});

			setUpdateEstimate(response.data?.estimate || null);
		} catch (error) {
			console.error('[CustomCheckoutModal] Error fetching update estimate:', error);
			setUpdateEstimate(null);
			setEstimateError(error.response?.data?.error || error.message || 'Unable to load order summary');
		} finally {
			setEstimateLoading(false);
		}
	};

	useEffect(() => {
		if (!open) return;

		setDataLoading(true);
		fetchSubscriptionData()
			.catch(error => {
				console.error('[CustomCheckoutModal] Error fetching modal data:', error);
			})
			.finally(() => setDataLoading(false));
	}, [open, resolvedPlanId]);

	useEffect(() => {
		if (!open) return;

		// Debug: log why/what we're fetching to help reproduce intermittent UI states
		console.debug('[CustomCheckoutModal] fetchUpdateEstimate triggered', {
			open,
			resolvedPlanId,
			resolvedIsCancelledUser,
			isCancelledUserProp: isCancelledUser,
		});

		fetchUpdateEstimate().catch(error => {
			console.error('[CustomCheckoutModal] Error fetching estimate data:', error);
		});
	}, [open, resolvedPlanId, resolvedIsCancelledUser]);

	useEffect(() => {
		if (!open) {
			setSelectedPaymentOption(savedPaymentMethods.length > 0 ? 'saved' : 'new');
			setSelectedNewPaymentType('card');
			setLoading(false);
			setShowSuccessScreen(false);
			setEstimateLoading(false);
			setUpdateEstimate(null);
			setEstimateError('');
			setErrors({});
			setCouponCode('');
			setCouponApplied(false);
			setCouponError('');
			setCouponEstimate(null);
		}
	}, [open, savedPaymentMethods.length]);

	const trackModalPaymentEvent = (eventName, paymentMethod, meta = {}) => {
		void trackPaymentUiClick({
			eventName,
			paymentMethod,
			planId: resolvedPlanId || plan?.id,
			source: 'plan_checkout_modal',
			meta
		});
	};

	const handleContinue = () => {
		onClose();
		window.location.reload();
	};

	const handleModalClose = () => {
		if (loading) return;
		if (showSuccessScreen) {
			handleContinue();
			return;
		}
		onClose();
	};

	const getChargebeeInstance = async () => {
		if (!window.Chargebee) {
			const existingScript = document.querySelector('script[data-chargebee-sdk="true"]');

			if (!existingScript) {
				const script = document.createElement('script');
				script.src = 'https://js.chargebee.com/v2/chargebee.js';
				script.async = true;
				script.dataset.chargebeeSdk = 'true';
				document.body.appendChild(script);
				await new Promise((resolve, reject) => {
					script.onload = resolve;
					script.onerror = reject;
				});
			} else {
				await new Promise(resolve => {
					if (window.Chargebee) {
						resolve();
						return;
					}
					existingScript.addEventListener('load', resolve, { once: true });
				});
			}
		}

		return window.Chargebee.init({
			site: import.meta.env.VITE_CHARGEBEE_SITE,
			publishableKey: import.meta.env.VITE_APP_CHARGEBEE_API_KEY
		});
	};

	const handleInputChange = (field, value) => {
		if (field === 'cardNumber') {
			value = value
				.replace(/\s/g, '')
				.replace(/(\d{4})/g, '$1 ')
				.trim();
			if (value.length > 19) return;
		}

		if (field === 'cardExpiry') {
			value = value.replace(/\D/g, '');
			if (value.length >= 2) {
				value = `${value.slice(0, 2)}/${value.slice(2, 4)}`;
			}
			if (value.length > 5) return;
		}

		if (field === 'cardCvv') {
			value = value.replace(/\D/g, '').slice(0, 4);
		}

		if (field === 'zipCode') {
			value = value.slice(0, 10);
			const detectedCountry = detectCountryFromZip(value);
			setPaymentData(prev => ({ ...prev, zipCode: value, country: detectedCountry }));
			setErrors(prev => ({ ...prev, zipCode: '', country: '' }));
			return;
		}

		setPaymentData(prev => ({ ...prev, [field]: value }));
		setErrors(prev => ({ ...prev, [field]: '' }));
	};

	const detectCountryFromZip = zip => {
		if (!zip || !zip.trim()) return 'US';
		const cleanedZip = zip.trim().toUpperCase();

		if (/^\d{6}$/.test(cleanedZip)) return 'IN';
		if (/^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i.test(cleanedZip)) return 'GB';
		if (/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(cleanedZip)) return 'CA';
		if (/^\d{4}$/.test(cleanedZip)) return 'AU';
		if (/^\d{5}(-?\d{4})?$/.test(cleanedZip)) return 'US';

		return 'US';
	};

	const validateZipCode = zip => {
		if (!zip || !zip.trim()) return false;
		const country = detectCountryFromZip(zip);
		const patterns = {
			US: /^\d{5}(-\d{4})?$/,
			AU: /^\d{4}$/,
			CA: /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i,
			GB: /^[A-Z]{1,2}\d{1,2}[A-Z]? ?\d[A-Z]{2}$/i,
			NZ: /^\d{4}$/,
			IN: /^\d{6}$/
		};

		if (!patterns[country]) {
			return zip.length >= 3 && zip.length <= 10;
		}

		return patterns[country].test(zip.trim());
	};

	const validatePaymentData = () => {
		const newErrors = {};

		if (!paymentData.cardholderName.trim()) {
			newErrors.cardholderName = 'Cardholder name is required';
		}

		const cardNum = paymentData.cardNumber.replace(/\s/g, '');
		if (!cardNum || cardNum.length < 13 || cardNum.length > 19) {
			newErrors.cardNumber = 'Invalid card number';
		}

		if (!paymentData.cardExpiry || paymentData.cardExpiry.length !== 5) {
			newErrors.cardExpiry = 'Invalid expiry date (MM/YY)';
		}

		if (!paymentData.cardCvv || paymentData.cardCvv.length < 3) {
			newErrors.cardCvv = 'Invalid CVV';
		}

		if (!paymentData.zipCode.trim()) {
			newErrors.zipCode = 'Zip/Postal code is required';
		} else if (!validateZipCode(paymentData.zipCode)) {
			newErrors.zipCode = 'Invalid zip/postal code format';
		}

		if (!paymentData.country) {
			newErrors.country = 'Country is required';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handlePaymentSourceUpdated = async () => {
		await fetchSubscriptionData();
		await fetchUpdateEstimate();
		setSelectedPaymentOption('saved');
		setSelectedNewPaymentType('card');
		setPaymentData({
			cardholderName: '',
			cardNumber: '',
			cardExpiry: '',
			cardCvv: '',
			zipCode: '',
			country: 'US'
		});
		setErrors({});
	};

	const handleUpgradeDowngradeSavedMethod = async paymentMethodTypeOverride => {
		const selectedPaymentMethod = savedPaymentMethods.find(
			(method, index) => getPaymentMethodId(method, index) === selectedPaymentMethodId
		);
		const paymentMethodType =
			paymentMethodTypeOverride ||
			(selectedPaymentMethod ? getNormalizedPaymentMethodType(selectedPaymentMethod) : 'payment_method');

		trackModalPaymentEvent('upgrade_downgrade_saved_method_clicked', paymentMethodType);

		if (!paymentMethodTypeOverride && !selectedPaymentMethod) {
			Toast.error('Please select a payment method');
			return;
		}

		setLoading(true);

		try {
			const userData = JSON.parse(localStorage.getItem('userData') || '{}');
			persistMetaCookiesToStorage();
			const trackingIds = getAllTrackingIds();
			const payload = {
				email: userData.email,
				planId: resolvedPlanId,
				...trackingIds
			};
			if (couponCode && couponApplied) {
				payload.couponCode = couponCode;
			}
			if (resolvedIsCancelledUser) {
				payload.isCancelledUser = true;
			}
			const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/update-subscription`, payload);

			trackModalPaymentEvent('upgrade_downgrade_success', paymentMethodType, {
				subscriptionId: response.data?.subscription?.id
			});

			if (response.data?.subscription?.id) {
				const currentUserData = JSON.parse(localStorage.getItem('userData') || '{}');
				const updatedUserData = {
					...currentUserData,
					userType: response.data.userType || currentUserData.userType || '',
					subscription: true,
					requiredSeats: response.data.requiredSeats || 0,
					availableSeats: response.data.availableSeats || 0,
					isTrialAvailed: true
				};
				localStorage.setItem('userData', JSON.stringify(updatedUserData));
				localStorage.setItem('subscriptionId', response.data.subscription.id);
				setUserData(prev => ({
					...prev,
					subscriptionId: response.data.subscription.id,
					user: updatedUserData,
					userType: response.data.userType || ''
				}));
			}

			setLoading(false);
			setShowSuccessScreen(true);
		} catch (error) {
			console.error('[CustomCheckoutModal] Error during upgrade/downgrade:', error);
			setLoading(false);
			const errorMsg = error.response?.data?.error || error.message || 'Update failed';
			trackModalPaymentEvent('upgrade_downgrade_failed', paymentMethodType, { error: errorMsg });
			Toast.error(errorMsg);
		}
	};

	const handleCardPaymentSourceUpdate = async () => {
		trackModalPaymentEvent('change_payment_method_card_clicked', 'card');

		if (!validatePaymentData()) {
			Toast.error('Please fill all payment fields correctly');
			return;
		}

		setLoading(true);

		try {
			const userData = JSON.parse(localStorage.getItem('userData') || '{}');
			const [expMonth, expYear] = paymentData.cardExpiry.split('/');

			await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/payment-sources/card`, {
				email: userData.email,
				cardNumber: paymentData.cardNumber.replace(/\s/g, ''),
				cardExpMonth: expMonth,
				cardExpYear: expYear,
				cardCvv: paymentData.cardCvv,
				cardholderName: paymentData.cardholderName,
				billingZip: paymentData.zipCode,
				billingCountry: paymentData.country,
				isCancelledUser: resolvedIsCancelledUser
			});

			await handlePaymentSourceUpdated();
			Toast.success('Payment method updated successfully!');
			await handleUpgradeDowngradeSavedMethod('card');
		} catch (error) {
			console.error('[CustomCheckoutModal] Error updating card payment source:', error);
			setLoading(false);
			Toast.error(error.response?.data?.error || error.message || 'Failed to update card payment source');
		}
	};

	useEffect(() => {
		if (!open || selectedPaymentOption !== 'new' || selectedNewPaymentType !== 'paypal') return;
		if (paypalInitialized.current) return;

		let isActive = true;

		const initPaypal = async () => {
			setPaypalLoading(true);

			try {
				const userData = JSON.parse(localStorage.getItem('userData') || '{}');
				const cb = await getChargebeeInstance();
				const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/paypal-payment-intent`, {
					amount: getPlanAmountInCents(plan),
					currency_code: 'USD',
					email: userData.email,
					firstName: userData.firstname || userData.firstName || '',
					lastName: userData.lastname || userData.lastName || ''
				});

				if (!isActive) return;

				const paymentIntent = response.data?.payment_intent;
				const paypalHandler = await cb.load('paypal');
				paypalHandler.setPaymentIntent(paymentIntent);

				const container = document.getElementById('checkout-paypal-container');
				if (container) {
					container.innerHTML = '';
				}

				await paypalHandler.mountPaymentButton('#checkout-paypal-container', {
					style: {
						layout: 'vertical',
						color: 'white',
						shape: 'rect',
						label: 'paypal',
						size: 'responsive'
					}
				});

				if (!isActive) return;

				paypalInitialized.current = true;
				setPaypalLoading(false);

				paypalHandler
					.handlePayment()
					.then(async authorizedIntent => {
						if (!authorizedIntent || authorizedIntent.status !== 'authorized') {
							if (isActive) {
								Toast.error('PayPal authorization was not completed.');
							}
							return;
						}

						setLoading(true);

						try {
							await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/payment-sources/payment-intent`, {
								email: userData.email,
								paymentIntentId: authorizedIntent.id,
								paymentMethodType: 'paypal_express_checkout'
							});

							await handlePaymentSourceUpdated();
							Toast.success('Payment method updated successfully!');
							await handleUpgradeDowngradeSavedMethod('paypal');
						} catch (error) {
							console.error('[CustomCheckoutModal] Error updating PayPal payment source:', error);
							setLoading(false);
							Toast.error(error.response?.data?.error || error.message || 'Failed to update PayPal payment source');
						}
					})
					.catch(error => {
						console.error('[CustomCheckoutModal] PayPal flow failed:', error);
						if (isActive) {
							setPaypalLoading(false);
							Toast.error(error.message || 'Unable to initialize PayPal');
						}
					});
			} catch (error) {
				console.error('[CustomCheckoutModal] Error initializing PayPal:', error);
				if (isActive) {
					setPaypalLoading(false);
					Toast.error(error.response?.data?.error || error.message || 'Unable to initialize PayPal');
				}
			}
		};

		initPaypal();

		return () => {
			isActive = false;
			paypalInitialized.current = false;
			const container = document.getElementById('checkout-paypal-container');
			if (container) {
				container.innerHTML = '';
			}
		};
	}, [open, plan, selectedNewPaymentType, selectedPaymentOption]);

	useEffect(() => {
		if (!open || selectedPaymentOption !== 'new' || selectedNewPaymentType !== 'apple_pay') return;
		if (applePayMounted.current) return;

		let isActive = true;

		const initApplePay = async () => {
			try {
				const userData = JSON.parse(localStorage.getItem('userData') || '{}');
				const cb = await getChargebeeInstance();
				const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/create-payment-intent`, {
					amount: getPlanAmountInCents(plan),
					currency_code: 'USD',
					payment_method_type: 'apple_pay',
					planId: resolvedPlanId || plan?.id,
					email: userData.email,
					firstName: userData.firstname || userData.firstName || '',
					lastName: userData.lastname || userData.lastName || ''
				});

				if (!isActive) return;

				const paymentIntent = response.data?.payment_intent;
				const applePayHandler = await cb.load('apple-pay');
				applePayHandler.setPaymentIntent(paymentIntent);

				let available = true;
				if (typeof applePayHandler.canMakePayments === 'function') {
					const capability = await applePayHandler.canMakePayments();
					available = capability !== false;
				}

				if (!available) {
					setApplePayAvailable(false);
					return;
				}

				setApplePayAvailable(true);
				applePayMounted.current = true;

				if (applePayContainerRef.current) {
					applePayContainerRef.current.innerHTML = '';
				}

				await applePayHandler.mountPaymentButton('#checkout-apple-pay-container');

				const authorizedIntent = await applePayHandler.handlePayment();
				if (!isActive) return;

				if (!authorizedIntent || authorizedIntent.status !== 'authorized') {
					Toast.error('Apple Pay authorization was not completed.');
					return;
				}

				setLoading(true);

				try {
					await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/payment-sources/payment-intent`, {
						email: userData.email,
						paymentIntentId: authorizedIntent.id,
						paymentMethodType: 'apple_pay'
					});

					await handlePaymentSourceUpdated();
					Toast.success('Payment method updated successfully!');
					await handleUpgradeDowngradeSavedMethod('apple_pay');
				} catch (error) {
					console.error('[CustomCheckoutModal] Error updating Apple Pay payment source:', error);
					setLoading(false);
					Toast.error(error.response?.data?.error || error.message || 'Failed to update Apple Pay payment source');
				}
			} catch (error) {
				console.error('[CustomCheckoutModal] Error initializing Apple Pay:', error);
				if (isActive) {
					setApplePayAvailable(false);
					Toast.error(error.response?.data?.error || error.message || 'Unable to initialize Apple Pay');
				}
			}
		};

		initApplePay();

		return () => {
			isActive = false;
			applePayMounted.current = false;
			if (applePayContainerRef.current) {
				applePayContainerRef.current.innerHTML = '';
			}
		};
	}, [open, plan, selectedNewPaymentType, selectedPaymentOption]);

	if (!open) return null;

	const estimateCurrencyCode = updateEstimate?.currencyCode || 'USD';
	const summaryItems = updateEstimate?.summaryItems || [];
	const changeType = updateEstimate?.changeType;
	const visibleSummaryItems = summaryItems.filter(item => item.kind !== 'credit' && item.kind !== 'credit_applied');
	const billingCycleLabel = getBillingCycleLabel(plan);
	const recurringChargeLabel = `New ${billingCycleLabel} Charge`;
	const nextRenewalLabel = `Next ${billingCycleLabel} Renewal`;
	const fullPlanPriceAmount = plan?.price
		? Math.round(Number(plan.price) * 100)
		: updateEstimate?.nextRenewalAmount || 0;
	const creditsAppliedToday = Math.abs(Number(updateEstimate?.creditsAppliedNow) || 0);
	const existingRefundableCredits = Number(updateEstimate?.customerCredits?.refundableCredits) || 0;
	const creditsGeneratedThisChange = Number(updateEstimate?.refundableCredits ?? updateEstimate?.generatedCredits) || 0;
	const totalCreditsAvailable = existingRefundableCredits + creditsGeneratedThisChange;
	const grossDueNow = (Number(updateEstimate?.dueNow) || 0) + creditsAppliedToday;
	const crossFamilyCreditBasis = Math.min(grossDueNow, fullPlanPriceAmount || grossDueNow);
	const creditsAppliedDisplay =
		changeType === 'cross_family' ? Math.min(totalCreditsAvailable, crossFamilyCreditBasis) : creditsAppliedToday;
	const dueNowDisplay =
	    changeType === 'cross_family'
	        ? Math.max(0, grossDueNow - creditsAppliedDisplay)
	        : Number(updateEstimate?.dueNow) || 0;

	// Coupon discount (minor units) when estimate-with-coupon was returned
	const couponDiscount = Math.abs(Number(couponEstimate?.discount) || 0);
	// When a coupon estimate is present use its dueNow for the balance to keep totals authoritative
	const effectiveDueNowDisplay = couponEstimate && couponApplied ? Number(couponEstimate.dueNow) : dueNowDisplay;

	// Compute a balance for the breakdown view: prefer coupon-derived dueNow when coupon applied,
	// otherwise compute from recurring/new charge minus credits applied.
	const breakdownBalanceDisplay =
		couponEstimate && couponApplied
			? effectiveDueNowDisplay
			: Math.max(0, (fullPlanPriceAmount || 0) - creditsAppliedDisplay - 0);
	const remainingCreditsAfterToday = Math.max(0, totalCreditsAvailable - creditsAppliedDisplay);

	const getCouponErrorMessage = (error, fallback = 'Failed to apply coupon') => {
		const rawMessage =
			error?.response?.data?.error ||
			error?.response?.data?.message ||
			error?.message ||
			fallback;

		return String(rawMessage).replace(/^subscription\[coupon\]\s*:\s*/i, '').trim();
	};

	const handleApplyCoupon = async () => {
		const normalizedCouponCode = couponCode.trim();

		if (!normalizedCouponCode) {
			setCouponError('Please enter a coupon code');
			return;
		}

		setCouponLoading(true);
		setCouponError('');
		setCouponApplied(false);
		setCouponEstimate(null);

		try {
			const userData = JSON.parse(localStorage.getItem('userData') || '{}');
			const userEmail = userData.email || userData.userEmail;
			if (!userEmail) {
				setCouponError('Email is required');
				setCouponLoading(false);
				return;
			}
			const response = await axiosConfig.post(
				`${import.meta.env.VITE_APP_API_URL}/chargebee/apply-coupon`,
				{
					email: userEmail,
					couponCode: normalizedCouponCode,
					planId: resolvedPlanId,
					isCancelledUser: resolvedIsCancelledUser
				}
			);
			
			if (resolvedIsCancelledUser) {
				const estimateResponse = await axiosConfig.post(
					`${import.meta.env.VITE_APP_API_URL}/chargebee/estimate-with-coupon`,
					{
						email: userEmail,
						planId: resolvedPlanId,
						couponCode: normalizedCouponCode
					}
				);
				setCouponEstimate(estimateResponse.data?.estimate || null);
			} else {
				await fetchUpdateEstimate();
			}

			setCouponApplied(true);
			Toast.success(response?.data?.message || 'Coupon validated successfully!');
		} catch (error) {
			const errorMsg = getCouponErrorMessage(error);
			setCouponApplied(false);
			setCouponEstimate(null);
			setCouponError(errorMsg);
			Toast.error(errorMsg);
		} finally {
			setCouponLoading(false);
		}
	};

	const handlePrimaryAction = async () => {
		if (!resolvedIsCancelledUser && changeType === 'same_plan') return;

		if (selectedPaymentOption === 'saved') {
			await handleUpgradeDowngradeSavedMethod();
			return;
		}

		if (selectedNewPaymentType === 'card') {
			await handleCardPaymentSourceUpdate();
		}
	};

	return createPortal(
		<div className="checkout-modal-overlay">
			<div className="checkout-modal-container">
				<div className="checkout-modal-header">
					<div className="checkout-modal-header__left">
						<h2>
							{showSuccessScreen
								? 'Subscription Updated'
								: resolvedIsCancelledUser
									? 'Re-subscribe'
							: 'Upgrade/Downgrade Plan'}
						</h2>
					</div>
					<button className="checkout-close-btn" onClick={handleModalClose} disabled={loading} aria-label="Close">
						&#x2715;
					</button>
				</div>

				<div className="checkout-body">
					{showSuccessScreen ? (
						<div className="checkout-success-screen">
							<h3>Success!</h3>
							<p>Your subscription has been updated successfully.</p>
							<button
								type="button"
								className="checkout-submit checkout-success-screen__button"
								onClick={handleContinue}
							>
								Continue
							</button>
						</div>
					) : dataLoading ? (
						<div className="checkout-spinner-wrap">
							<div className="checkout-spinner" />
						</div>
					) : subscriptionStatus?.hasSubscription || resolvedIsCancelledUser ? (
						<div className="checkout-upgrade-section">
							<div className="checkout-order-summary">
								<div className="checkout-order-summary__header">
									<div>
										<h4>Order Summary</h4>
									</div>
									{estimateLoading && <span className="checkout-order-summary__badge">Loading...</span>}
								</div>

								{!estimateLoading && !estimateError && updateEstimate ? (
									<>
										{changeType === 'same_plan' ? (
											<div className="checkout-order-summary__notice">
												You are already on this plan. No additional charge is expected.
											</div>
										) : (
											<>
												<div className="checkout-order-summary__grid">
													<div className="checkout-order-summary__card">
														<span className="checkout-order-summary__label">Due Now</span>
														<strong>{formatMinorUnitAmount(effectiveDueNowDisplay, estimateCurrencyCode)}</strong>
													</div>
													<div className="checkout-order-summary__card">
														<span className="checkout-order-summary__label">Pro-rated Credits</span>
														<strong>{formatMinorUnitAmount(totalCreditsAvailable, estimateCurrencyCode)}</strong>
													</div>
													<div className="checkout-order-summary__card">
														<span className="checkout-order-summary__label">{nextRenewalLabel}</span>
														<strong>{formatMinorUnitAmount(fullPlanPriceAmount, estimateCurrencyCode)}</strong>
													</div>
													<div className="checkout-order-summary__card">
														<span className="checkout-order-summary__label">Next Billing Date</span>
														<strong>{formatUnixDate(updateEstimate.nextBillingAt)}</strong>
													</div>
												</div>

												<div className="checkout-order-summary__list">
													<div className="checkout-order-summary__list-item">
														<span>{recurringChargeLabel}</span>
														<strong>{formatMinorUnitAmount(fullPlanPriceAmount, estimateCurrencyCode)}</strong>
													</div>

													<div className="checkout-order-summary__list-item">
														<span>Credits Applied Today</span>
														<strong>{formatMinorUnitAmount(creditsAppliedDisplay, estimateCurrencyCode)}</strong>
													</div>

													{couponEstimate && couponDiscount > 0 && (
														<div className="checkout-order-summary__list-item">
															<span>Coupon Discount</span>
															<strong>{formatMinorUnitAmount(couponDiscount, estimateCurrencyCode)}</strong>
														</div>
													)}

													{remainingCreditsAfterToday > 0 ? (
														<div
															style={{
																fontSize: '0.8em',
																color: '#666',
																marginTop: '8px',
																padding: '8px 10px',
																backgroundColor: '#f0f9ff',
																borderLeft: '3px solid #0ea5e9',
																borderRadius: '3px',
																lineHeight: '1.5'
															}}
														>
															Remaining{' '}
															<strong style={{ color: '#0ea5e9' }}>
																{formatMinorUnitAmount(remainingCreditsAfterToday, estimateCurrencyCode)}
															</strong>{' '}
															will be used for future invoices
														</div>
													) : null}
												</div>

												<div className="checkout-order-summary__list checkout-order-summary__list--totals">
													<div className="checkout-order-summary__list-item checkout-order-summary__list-item--highlight">
														<span>Balance Payable Today</span>
														<strong>{formatMinorUnitAmount(breakdownBalanceDisplay, estimateCurrencyCode)}</strong>
													</div>
												</div>
											</>
										)}
									</>
								) : estimateLoading ? (
									<div className="checkout-order-summary__notice">Loading order summary...</div>
								) : estimateError ? (
									<div className="checkout-order-summary__notice checkout-order-summary__notice--error">
										{estimateError}
									</div>
								) : null}

								{resolvedIsCancelledUser && (
									<div className="checkout-coupon-section">
										<div className="checkout-coupon-header">
											<h4>Apply Coupon</h4>
										</div>
										<div className="checkout-coupon-input-group">
											<input
												type="text"
												className="checkout-coupon-input"
												placeholder="Enter coupon code"
												value={couponCode}
												onChange={e => {
													setCouponCode(e.target.value);
													setCouponError('');
												}}
												disabled={couponLoading || couponApplied}
											/>
											<button
												type="button"
												className="checkout-coupon-btn"
												onClick={handleApplyCoupon}
												disabled={couponLoading || couponApplied || !couponCode.trim()}
											>
												{couponLoading ? 'Applying...' : couponApplied ? 'Applied' : 'Apply Coupon'}
											</button>
										</div>
										{couponError && <p className="checkout-coupon-error">{couponError}</p>}
										{couponApplied && <p className="checkout-coupon-success">Coupon applied successfully</p>}
									</div>
								)}
							</div>

							<div className="checkout-payment-panel">
								<div className="checkout-payment-panel__header">
									<h4>Payment Methods</h4>
								</div>

								<label
									className={`checkout-payment-option ${selectedPaymentOption === 'saved' ? 'is-selected' : ''} ${savedPaymentMethods.length === 0 ? 'is-disabled' : ''}`}
								>
									<div className="checkout-payment-option__header">
										<input
											type="radio"
											name="checkoutPaymentOption"
											checked={selectedPaymentOption === 'saved'}
											onChange={() => savedPaymentMethods.length > 0 && setSelectedPaymentOption('saved')}
											disabled={loading || savedPaymentMethods.length === 0}
										/>
										<div>
											<strong>Option 1: Use Saved Payment Method</strong>
											<p>
												{savedPaymentMethods.length > 0
													? 'Use an existing saved method for this plan update.'
													: 'No saved payment methods found. Choose option 2 to add one.'}
											</p>
										</div>
									</div>

									{savedPaymentMethods.length > 0 && selectedPaymentOption === 'saved' && (
										<div className="checkout-payment-method-list">
											{savedPaymentMethods.map((method, index) => (
												<div key={getPaymentMethodId(method, index)} className="checkout-payment-method-item">
													<input
														type="radio"
														id={`method-${index}`}
														name="paymentMethod"
														value={index}
														checked={selectedPaymentMethodId === getPaymentMethodId(method, index)}
														onChange={() => setSelectedPaymentMethodId(getPaymentMethodId(method, index))}
													/>
													<label htmlFor={`method-${index}`}>{getSavedPaymentMethodLabel(method)}</label>
												</div>
											))}
										</div>
									)}
								</label>

								<label className={`checkout-payment-option ${selectedPaymentOption === 'new' ? 'is-selected' : ''}`}>
									<div className="checkout-payment-option__header">
										<input
											type="radio"
											name="checkoutPaymentOption"
											checked={selectedPaymentOption === 'new'}
											onChange={() => setSelectedPaymentOption('new')}
											disabled={loading}
										/>
										<div>
											<strong>Option 2: Change Payment Method</strong>
											<p>Choose a new payment method below and save it directly from this page.</p>
										</div>
									</div>

									{selectedPaymentOption === 'new' && (
										<>
											<div className="checkout-method-selector">
												<button
													type="button"
													className={`checkout-method-chip ${selectedNewPaymentType === 'card' ? 'is-active' : ''}`}
													onClick={() => setSelectedNewPaymentType('card')}
													disabled={loading}
												>
													Card
												</button>
												<button
													type="button"
													className={`checkout-method-chip ${selectedNewPaymentType === 'paypal' ? 'is-active' : ''}`}
													onClick={() => setSelectedNewPaymentType('paypal')}
													disabled={loading}
												>
													PayPal
												</button>
												{/* <button
                          type="button"
                          className={`checkout-method-chip ${selectedNewPaymentType === 'apple_pay' ? 'is-active' : ''}`}
                          onClick={() => setSelectedNewPaymentType('apple_pay')}
                          disabled={loading}
                        >
                          Apple Pay
                        </button> */}
											</div>

											{selectedNewPaymentType === 'card' && (
												<>
													<div className="checkout-form-grid">
														<div>
															<label className="checkout-label" htmlFor="upgradeCardholderName">
																Cardholder Name
															</label>
															<div className="checkout-input-with-icon">
																<PersonOutlineOutlinedIcon className="checkout-input-icon" />
																<input
																	id="upgradeCardholderName"
																	className="checkout-input checkout-input--with-icon"
																	type="text"
																	placeholder="Jane Doe"
																	value={paymentData.cardholderName}
																	onChange={e => handleInputChange('cardholderName', e.target.value)}
																	disabled={loading}
																/>
															</div>
															{errors.cardholderName && <p className="checkout-error-text">{errors.cardholderName}</p>}
														</div>

														<div className="checkout-card-country-row">
															<div>
																<label className="checkout-label" htmlFor="upgradeCardNumber">
																	Card Number
																</label>
																<div className="checkout-input-with-icon">
																	<CreditCardOutlinedIcon className="checkout-input-icon" />
																	<input
																		id="upgradeCardNumber"
																		className="checkout-input checkout-input--with-icon"
																		type="text"
																		placeholder="XXXX XXXX XXXX XXXX"
																		value={paymentData.cardNumber}
																		onChange={e => handleInputChange('cardNumber', e.target.value)}
																		disabled={loading}
																	/>
																</div>
																{errors.cardNumber && <p className="checkout-error-text">{errors.cardNumber}</p>}
															</div>

															<div>
																<label className="checkout-label" htmlFor="upgradeCountry">
																	Country
																</label>
																<div className="checkout-input-with-icon">
																	<select
																		id="upgradeCountry"
																		className="checkout-input checkout-input--with-icon"
																		value={paymentData.country}
																		onChange={e => handleInputChange('country', e.target.value)}
																		disabled={loading}
																	>
																		{COUNTRIES.map(country => (
																			<option key={country.code} value={country.code}>
																				{country.name}
																			</option>
																		))}
																	</select>
																</div>
																{errors.country && <p className="checkout-error-text">{errors.country}</p>}
															</div>
														</div>

														<div className="checkout-small-row">
															<div>
																<label className="checkout-label" htmlFor="upgradeCardExpiryDate">
																	Card Expiry Date
																</label>
																<div className="checkout-input-with-icon">
																	<CalendarMonthOutlinedIcon className="checkout-input-icon" />
																	<input
																		id="upgradeCardExpiryDate"
																		className="checkout-input checkout-input--with-icon"
																		type="text"
																		placeholder="MM/YY"
																		value={paymentData.cardExpiry}
																		onChange={e => handleInputChange('cardExpiry', e.target.value)}
																		disabled={loading}
																	/>
																</div>
																{errors.cardExpiry && <p className="checkout-error-text">{errors.cardExpiry}</p>}
															</div>

															<div>
																<label className="checkout-label" htmlFor="upgradeCardCvv">
																	CVV
																</label>
																<div className="checkout-input-with-icon">
																	<AddCardOutlinedIcon className="checkout-input-icon" />
																	<input
																		id="upgradeCardCvv"
																		className="checkout-input checkout-input--with-icon"
																		type="text"
																		placeholder="CVV"
																		value={paymentData.cardCvv}
																		onChange={e => handleInputChange('cardCvv', e.target.value)}
																		disabled={loading}
																	/>
																</div>
																{errors.cardCvv && <p className="checkout-error-text">{errors.cardCvv}</p>}
															</div>

															<div>
																<label className="checkout-label" htmlFor="upgradeZipCode">
																	Zip/Postal Code
																</label>
																<div className="checkout-input-with-icon">
																	<NearMeOutlinedIcon className="checkout-input-icon" />
																	<input
																		id="upgradeZipCode"
																		className="checkout-input checkout-input--with-icon"
																		type="text"
																		placeholder="#####"
																		value={paymentData.zipCode}
																		onChange={e => handleInputChange('zipCode', e.target.value)}
																		disabled={loading}
																	/>
																</div>
																{errors.zipCode && <p className="checkout-error-text">{errors.zipCode}</p>}
															</div>
														</div>
													</div>
												</>
											)}

											{selectedNewPaymentType === 'paypal' && (
												<div className="checkout-wallet-section">
													<p>Authorize PayPal below to save it as your payment method.</p>
													{paypalLoading && <div className="checkout-wallet-loading">Loading PayPal...</div>}
													<div className="checkout-wallet-row">
														<div className="checkout-wallet-btn-wrapper">
															<div
																id="checkout-paypal-container"
																style={{ display: paypalLoading ? 'none' : 'block', width: '100%', height: '100%' }}
															></div>
														</div>
													</div>
												</div>
											)}

											{selectedNewPaymentType === 'apple_pay' && (
												<div className="checkout-wallet-section">
													<p>Use Apple Pay below to save it as your payment method.</p>
													{!applePayAvailable && (
														<div className="checkout-wallet-loading">
															Apple Pay is only available on supported Apple devices and browsers.
														</div>
													)}
													<div className="checkout-wallet-row">
														<div
															id="checkout-apple-pay-container"
															ref={applePayContainerRef}
															className="checkout-wallet-btn checkout-wallet-btn--apple"
														/>
													</div>
												</div>
											)}
										</>
									)}
								</label>

								<div className="checkout-action-row">
									<button
										type="button"
										className="checkout-submit"
										onClick={handlePrimaryAction}
										disabled={
											loading ||
											(!resolvedIsCancelledUser && changeType === 'same_plan') ||
											(selectedPaymentOption === 'saved' && !selectedPaymentMethodId) ||
											(selectedPaymentOption === 'new' && selectedNewPaymentType !== 'card')
										}
									>
										{loading
											? 'Processing...'
											: resolvedIsCancelledUser
												? 'Subscribe Now'
												: updateEstimate?.dueNow === 0
													? 'Confirm Changes'
													: 'Update & Pay Now'}
									</button>
									<button type="button" className="checkout-cancel-btn" onClick={handleModalClose} disabled={loading}>
										Cancel
									</button>
								</div>
							</div>
						</div>
					) : (
						<div style={{ padding: '20px', textAlign: 'center' }}>
							<p>No active subscription found. Please subscribe first.</p>
							<button type="button" className="checkout-submit" onClick={() => navigate('/plans')}>
								Go to Plans
							</button>
						</div>
					)}
				</div>
			</div>
		</div>,
		document.body
	);
}
